import PropTypes from "prop-types";
import { useMemo } from "react";
// @mui
import { CssBaseline, responsiveFontSizes } from "@mui/material";
import {
  ThemeProvider as MUIThemeProvider,
  createTheme,
  StyledEngineProvider,
} from "@mui/material/styles";
//
import palette from "./palette";
import shadows from "./shadows";
import typography from "./typography";
import GlobalStyles from "./globalStyles";
import customShadows from "./customShadows";

// ----------------------------------------------------------------------

const ThemeProvider = (props) => {
  const { children } = props;

  const themeOptions = useMemo(
    () => ({
      palette,
      shape: { borderRadius: 6 },
      typography,
      shadows: shadows(),
      customShadows: customShadows(),
    }),
    []
  );

  const theme = createTheme(themeOptions);
  theme.components = {
    MuiCssBaseline: {
      styleOverrides: `
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        textarea:-webkit-autofill,
        textarea:-webkit-autofill:hover,
        textarea:-webkit-autofill:focus,
        select:-webkit-autofill,
        select:-webkit-autofill:hover,
        select:-webkit-autofill:focus {
          -webkit-text-fill-color: #0000000;
          -webkit-box-shadow: 0 0 0px 1000px transparent inset;
          transition: background-color 5000s ease-in-out 0s;
        }
      `,
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          border: "none !important",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 32,
          padding: "12px 24px !important",
          height: 42,
          fontSize: "16px !important",
          fontWeight: 500,
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: palette.primary.dark,
          },
        },
      },
    },
    MuiLoadingButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 32,
          padding: "8px 24px",
          height: 45,
          fontSize: "16px !important",
          fontWeight: 500,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: "#000",
          fontWeight: 500,
          fontSize: "15px",
          whiteSpace: "wrap !important",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: "32px !important",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 32,
          padding: "24px !important",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            textTransform: "none",
            borderRadius: 32,
            padding: "8px",
            height: 45,
            fontSize: "16px !important",
            fontWeight: 500,
            backgroundColor: "#f8f8f8",
            "& fieldset": {
              borderColor: "transparent",
            },
            "&:hover fieldset": {
              borderColor: palette.primary.main,
            },
            "&.Mui-focused fieldset": {
              borderColor: palette.primary.main,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          width: "100%",
          borderRadius: 32,
          padding: "8px",
          height: 45,
          fontSize: "16px!important",
          fontWeight: 500,
          backgroundColor: "#f8f8f8",
          "& fieldset": {
            borderColor: "transparent",
          },
          "&:hover fieldset": {
            borderColor: palette.primary.main,
          },
          "&.Mui-focused fieldset": {
            borderColor: palette.primary.main,
          },
        },
        select: {
          "&:has(option:disabled:checked)": {
            color: "#9e9e9e !important",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&::placeholder": {
            color: "#9e9e9e !important",
            opacity: 1,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid rgba(140, 123, 255, 0.12)",
          padding: "25px",
          borderRadius: 32,
          boxShadow: customShadows().card,
          "&.MuiPaper-outlined": {
            borderWidth: 1,
            border: "1px solid rgba(140, 123, 255, 0.12)",
          },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          borderRadius: 32,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 0,
          "&:last-child": {
            paddingBottom: 0,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "#000000",
          "&.Mui-checked": {
            color: theme.palette.secondary.main,
          },
        },
      },
    },
    // Added custom tab styles globally with 45px height
    MuiTabContext: {
      styleOverrides: {
        root: {
          width: "100%",
        },
      },
    },
    MuiTabList: {
      styleOverrides: {
        root: {
          height: "45px",
          minHeight: "45px",
          maxHeight: "45px",
          width: "100%",
          "& .MuiTabs-flexContainer": {
            borderRadius: 32,
            width: "100%",
            position: "relative",
            height: "45px",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          height: "45px",
          minHeight: "45px",
          "& .MuiTabs-indicator": {
            display: "none",
          },
        },
        scrollButtons: {
          color: palette.text.primary,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: palette.text.primary,
          textTransform: "none",
          fontSize: "15px",
          fontWeight: 500,
          height: "45px",
          minHeight: "45px",
          maxHeight: "45px",
          minWidth: "100px",
          lineHeight: "45px",
          borderRadius: 32,
          transition: "all 0.3s ease",
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: 1,
          "&.Mui-selected": {
            color: "#fff",
            backgroundColor: palette.tertiary.main,
            transition: "all 0.3s ease",
          },
        },
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
  };

  return (
    <StyledEngineProvider injectFirst>
      <MUIThemeProvider theme={responsiveFontSizes(theme)}>
        <CssBaseline />
        <GlobalStyles />
        {children}
      </MUIThemeProvider>
    </StyledEngineProvider>
  );
};

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

export default ThemeProvider;
